import Phaser from 'phaser';
import { COMPONENT_SPACING } from '@/config/GameConfig';
import { GOD } from '@/config/GodMode';
import { makeRenderTexture } from '@/utils/PhaserHelpers';
import type { RobotHost } from './RobotHost';

/**
 * Renders HP, Energy, and Shield bars above the robot, plus the save-mode indicator.
 */
export class RobotBarRenderer {
  // Bar sprites (scene-level, batchable 1x1 white pixel textures)
  private barBgHp?: Phaser.GameObjects.Sprite;
  private barBgEn?: Phaser.GameObjects.Sprite;
  private barBgShield?: Phaser.GameObjects.Sprite;
  private barFillHp?: Phaser.GameObjects.Sprite;
  private barFillEn?: Phaser.GameObjects.Sprite;
  private barFillShield?: Phaser.GameObjects.Sprite;
  private saveModeGfx?: Phaser.GameObjects.Graphics;
  private saveModeIcon?: Phaser.GameObjects.Image;
  private saveModeAlpha: number = 0;

  // Cache for bar state to avoid unnecessary updates
  private lastHpRatio: number = -1;
  private lastEnergyRatio: number = -1;
  private lastShieldRatio: number = -1;
  private lastBarX: number = -1;
  private lastBarY: number = -1;

  constructor(private host: RobotHost) {}

  /** Create bar sprites (called once during Robot construction) */
  createBars(): void {
    const scene = this.host.scene;
    const pixKey = '_px_white';
    if (!scene.textures.exists(pixKey)) {
      const rt = makeRenderTexture(scene, 1, 1);
      const gfx = scene.make.graphics({});
      gfx.fillStyle(0xffffff, 1);
      gfx.fillRect(0, 0, 1, 1);
      rt.draw(gfx, 0, 0);
      rt.saveTexture(pixKey);
      rt.destroy();
      gfx.destroy();
    }
    this.barBgHp?.destroy();
    this.barBgEn?.destroy();
    this.barBgShield?.destroy();
    this.barFillHp?.destroy();
    this.barFillEn?.destroy();
    this.barFillShield?.destroy();
    this.barBgHp = scene.add.sprite(0, 0, pixKey).setOrigin(0, 0).setDepth(31).setTint(0x3a3a3a);
    this.barBgEn = scene.add.sprite(0, 0, pixKey).setOrigin(0, 0).setDepth(31).setTint(0x3a3a3a);
    this.barBgShield = scene.add.sprite(0, 0, pixKey).setOrigin(0, 0).setDepth(31).setTint(0x3a3a3a);
    this.barFillHp = scene.add.sprite(0, 0, pixKey).setOrigin(0, 0).setDepth(32);
    this.barFillEn = scene.add.sprite(0, 0, pixKey).setOrigin(0, 0).setDepth(32).setTint(0xffcc00);
    this.barFillShield = scene.add.sprite(0, 0, pixKey).setOrigin(0, 0).setDepth(32).setTint(0x4488ff);

    // Save mode indicator
    this.saveModeGfx?.destroy();
    this.saveModeGfx = scene.add.graphics().setDepth(33);
    this.saveModeGfx.setVisible(false);
    this.saveModeIcon?.destroy();
    if (scene.textures.exists('icon_energy')) {
      this.saveModeIcon = scene.add.image(0, 0, 'icon_energy')
        .setDisplaySize(10, 10).setDepth(33).setVisible(false);
    }
  }

  /** Hide all bar sprites (for off-screen culling) */
  hideAll(): void {
    this.barBgHp?.setVisible(false);
    this.barBgEn?.setVisible(false);
    this.barBgShield?.setVisible(false);
    this.barFillHp?.setVisible(false);
    this.barFillEn?.setVisible(false);
    this.barFillShield?.setVisible(false);
    this.saveModeGfx?.setVisible(false);
    this.saveModeIcon?.setVisible(false);
  }

  /** Show bar backgrounds (for on-screen robots) */
  showBgs(): void {
    this.barBgHp?.setVisible(true);
    this.barBgEn?.setVisible(true);
    this.barFillHp?.setVisible(true);
    this.barFillEn?.setVisible(true);
  }

  /** Update bar rendering (call every frame for on-screen robots) */
  updateBars(): void {
    const sm = GOD.robotBarSizeMult;
    const barW = Math.max(12, this.host.physicsRadius * 1.4) * sm;
    const barH = 3 * sm;
    const barGap = barH;

    let topLocalY = 0;
    for (const c of this.host.components) {
      const cy = c.localY * GOD.componentSpacingMult;
      if (cy < topLocalY) topLocalY = cy;
    }
    const actualScale = Math.abs(this.host.container.scaleY);
    const halfComp = (COMPONENT_SPACING / 2) * actualScale;
    const topWorldOff = Math.abs(topLocalY) * actualScale + halfComp;
    const baseOff = (topWorldOff + 2) * GOD.robotBarOffsetMult;

    // Aggregate shield
    let totalShieldMax = 0;
    let totalShieldCurrent = 0;
    for (const c of this.host.components) {
      if (c.shieldMax > 0) {
        totalShieldMax += c.shieldMax;
        totalShieldCurrent += c.shieldCurrent;
      }
    }
    const hasShield = totalShieldMax > 0;

    const hpX = this.host.x - barW / 2;
    const shieldY = this.host.y - baseOff - barH / 2;
    const hpY = hasShield ? shieldY + barGap : this.host.y - baseOff - barH / 2;
    const enY = hpY + barGap;

    const hpRatio = this.host.totalHp > 0 ? Math.max(0, Math.min(1, this.host.currentHp / this.host.totalHp)) : 0;
    const eRatio = this.host.maxEnergy > 0 ? Math.max(0, Math.min(1, this.host.currentEnergy / this.host.maxEnergy)) : 0;
    const shieldRatio = totalShieldMax > 0 ? Math.max(0, Math.min(1, totalShieldCurrent / totalShieldMax)) : 0;
    const hpRatioSnap = Math.round(hpRatio * 200);
    const eRatioSnap = Math.round(eRatio * 200);
    const shieldRatioSnap = Math.round(shieldRatio * 200);
    const posXSnap = Math.round(this.host.x);
    const posYSnap = Math.round(this.host.y);
    const barsChanged = hpRatioSnap !== this.lastHpRatio || eRatioSnap !== this.lastEnergyRatio
      || shieldRatioSnap !== this.lastShieldRatio
      || posXSnap !== this.lastBarX || posYSnap !== this.lastBarY;

    if (barsChanged) {
      this.lastHpRatio = hpRatioSnap;
      this.lastEnergyRatio = eRatioSnap;
      this.lastShieldRatio = shieldRatioSnap;
      this.lastBarX = posXSnap;
      this.lastBarY = posYSnap;

      // Shield bar
      if (hasShield) {
        if (this.barBgShield) {
          this.barBgShield.setPosition(hpX, shieldY).setDisplaySize(barW, barH).setVisible(true);
        }
        if (this.barFillShield) {
          if (shieldRatio > 0) {
            const sFillW = barW * shieldRatio;
            this.barFillShield.setPosition(hpX, shieldY).setDisplaySize(sFillW, barH).setVisible(true);
          } else {
            this.barFillShield.setVisible(false);
          }
        }
      } else {
        this.barBgShield?.setVisible(false);
        this.barFillShield?.setVisible(false);
      }

      // Bar backgrounds
      if (this.barBgHp) {
        this.barBgHp.setPosition(hpX, hpY).setDisplaySize(barW, barH);
      }
      if (this.barBgEn) {
        this.barBgEn.setPosition(hpX, enY).setDisplaySize(barW, barH);
      }

      // Bar fills
      if (this.barFillHp) {
        if (this.host.totalHp > 0 && hpRatio > 0) {
          const hpColor = hpRatio > 0.5 ? 0x44ff44 : hpRatio > 0.25 ? 0xffaa00 : 0xff4444;
          const fillW = barW * hpRatio;
          this.barFillHp.setPosition(hpX, hpY).setDisplaySize(fillW, barH).setTint(hpColor).setVisible(true);
        } else {
          this.barFillHp.setVisible(false);
        }
      }
      if (this.barFillEn) {
        if (this.host.maxEnergy > 0 && eRatio > 0) {
          const eFillW = barW * eRatio;
          this.barFillEn.setPosition(hpX, enY).setDisplaySize(eFillW, barH).setVisible(true);
        } else {
          this.barFillEn.setVisible(false);
        }
      }
    }

    // Save mode indicator
    if (this.saveModeIcon) {
      if (this.host.energySaveMode && this.host.alive) {
        this.saveModeAlpha += 0.06;
        const pulse = 0.7 + 0.3 * Math.sin(this.saveModeAlpha * 3);
        const iconX = hpX + barW + 6;
        const iconY = enY + barH / 2;
        const iconSz = Math.max(6, barH * 2.5);
        this.saveModeIcon.setPosition(iconX, iconY).setDisplaySize(iconSz, iconSz).setAlpha(pulse).setVisible(true);
      } else {
        this.saveModeIcon.setVisible(false);
        this.saveModeAlpha = 0;
      }
    } else if (this.saveModeGfx) {
      if (this.host.energySaveMode && this.host.alive) {
        this.saveModeAlpha += 0.06;
        const pulse = 0.7 + 0.3 * Math.sin(this.saveModeAlpha * 3);
        this.saveModeGfx.clear();
        const iconX = hpX + barW + 3;
        const iconY = enY - 1;
        const s = sm * 0.8;
        this.saveModeGfx.fillStyle(0xffaa00, pulse);
        this.saveModeGfx.beginPath();
        this.saveModeGfx.moveTo(iconX + 3 * s, iconY);
        this.saveModeGfx.lineTo(iconX + 0 * s, iconY + 4 * s);
        this.saveModeGfx.lineTo(iconX + 2 * s, iconY + 4 * s);
        this.saveModeGfx.lineTo(iconX + 1 * s, iconY + 8 * s);
        this.saveModeGfx.lineTo(iconX + 4 * s, iconY + 3 * s);
        this.saveModeGfx.lineTo(iconX + 2 * s, iconY + 3 * s);
        this.saveModeGfx.closePath();
        this.saveModeGfx.fillPath();
        this.saveModeGfx.setVisible(true);
      } else {
        this.saveModeGfx.setVisible(false);
        this.saveModeAlpha = 0;
      }
    }
  }

  /** Invalidate cached bar state so next update forces a redraw */
  invalidateBars(): void {
    this.lastHpRatio = -1;
    this.lastEnergyRatio = -1;
    this.lastShieldRatio = -1;
    this.lastBarX = -1;
    this.lastBarY = -1;
  }

  /** Destroy scene-level HP/energy bars */
  destroyBars(): void {
    this.barBgHp?.destroy();
    this.barBgEn?.destroy();
    this.barBgShield?.destroy();
    this.barFillHp?.destroy();
    this.barFillEn?.destroy();
    this.barFillShield?.destroy();
    this.saveModeGfx?.destroy();
    this.saveModeIcon?.destroy();
    this.barBgHp = undefined;
    this.barBgEn = undefined;
    this.barBgShield = undefined;
    this.barFillHp = undefined;
    this.barFillEn = undefined;
    this.barFillShield = undefined;
    this.saveModeGfx = undefined;
    this.saveModeIcon = undefined;
  }
}
